import type {
  EntropySource,
  GameDefinition,
  SnapshotStore,
  TableClientState,
  TableServerOptions,
} from '@bgf/table';
import { TableClient } from '@bgf/table';
import type { TableServer } from '../runtime';
import { CURRENT_RUNTIME, runtimeFor, watchPin } from './runtimes';
import { newMailboxKey } from '../mailbox/crypto';
import type { MailRelay } from '../mailbox/offline';
import { relays } from '../mailbox/offline';
import { playedByMail } from '../mailbox/mutual';
import { CHECK_MAIL_EVENT } from '../mailbox/client';
import type {
  Listener,
  PlayerProfile,
  Signer,
  TableSnapshot,
  TransportProvider,
} from '@bgf/protocol';
import { TransportError, generateRoomCode } from '@bgf/protocol';
import { SessionError, publicProfile } from './session';
import type { BaseSession } from './SessionRegistry';
import type { RandomnessChoice } from './entropy';
import { buildEntropy, randomnessOptions } from './entropy';
import type { FlowDeps, FlowOptions } from './retry';
import { connLog, logStatusChanges } from './connLog';
import { cancelled, joinWithRetry, resumeWithRetry, throwIfAborted } from './retry';

/**
 * Sessions for games on the generic table core (`@bgf/table`). Backgammon predates the core and
 * keeps its own wrappers in `session.ts`; every newer game (OFC first) uses these, so adding a
 * game means supplying a `GameDefinition` and screens — nothing here changes.
 */

export type TableRole = 'host' | 'guest';

export interface TableSession<
  S = unknown,
  A = unknown,
  C = unknown,
  V = S,
  Cfg = unknown,
> extends BaseSession {
  role: TableRole;
  client: TableClient<S, A, Cfg, V>;
  server?: TableServer<S, A, C, V, Cfg>;
  provider: TransportProvider;
  gameId: string;
  /** Set while the host is away and this seat plays by mail (see `mailbox/offline.ts`). */
  byMail?: MailRelay;
}

/** Persistence the session layer needs: put every snapshot, and look copies up for resume/join. */
export interface TableStore<S = unknown, A = unknown, Cfg = unknown> extends SnapshotStore<
  S,
  A,
  Cfg
> {
  get?(id: string): Promise<TableSnapshot<S, A, Cfg> | undefined>;
  findByCode?(code: string): Promise<TableSnapshot<S, A, Cfg> | undefined>;
}

export interface TableSessionDeps<
  S = unknown,
  A = unknown,
  C = unknown,
  V = S,
  Cfg = unknown,
> extends FlowDeps {
  provider: TransportProvider;
  store?: TableStore<S, A, Cfg>;
  joinTimeoutMs?: number;
  /**
   * A session this tab already runs under a room code (the registry's live session). Resuming a
   * table we are hosting must hand that back instead of joining our own server as a guest.
   */
  existingSession?: (code: string) => TableSession<S, A, C, V, Cfg> | undefined;
  /** Overridable for tests. May be asynchronous (`TableServer.create`). */
  createServer?: (
    opts: ConstructorParameters<typeof TableServer<S, A, C, V, Cfg>>[0],
  ) => TableServer<S, A, C, V, Cfg> | Promise<TableServer<S, A, C, V, Cfg>>;
  createClient?: (
    opts: ConstructorParameters<typeof TableClient<S, A, Cfg, V>>[0],
  ) => TableClient<S, A, Cfg, V>;
  /**
   * Play a saved seat's view by mail while its host is away: the relay to use, or null when this
   * table or device cannot (no mailbox). Without it, an absent host means `host-offline`.
   */
  byMail?: (snapshot: TableSnapshot<S, A, Cfg>, seat: number) => Promise<MailRelay | null>;
}

const DEFAULT_JOIN_TIMEOUT = 20_000;

function requireName(profile: PlayerProfile): void {
  if (!profile.name.trim()) throw new SessionError('no-name', 'Pick a name before playing');
}

function rejectMessage(reason: string | null, seats: number | null): string {
  switch (reason) {
    case 'full':
      return seats
        ? `That table already has ${seats} players`
        : 'That table already has all its players';
    case 'protocol':
      return 'The host is running a different version of the game';
    case 'wrong-match':
      return 'Your saved copy does not belong to that host';
    case 'unauthorized':
      return 'That seat belongs to a different key: this browser cannot prove it is that player';
    default:
      return 'The host declined the connection';
  }
}

export function mapTableTransportError(e: unknown): SessionError {
  if (e instanceof SessionError) return e;
  if (e instanceof TransportError) {
    switch (e.code) {
      case 'not-found':
        return new SessionError('not-found', 'No one is hosting that code right now');
      case 'timeout':
        return new SessionError('timeout', 'Could not reach the host (timed out)');
      case 'address-taken':
        return new SessionError('address-taken', 'That code is already being hosted');
      case 'unsupported':
        return new SessionError(
          'unsupported',
          'This browser does not support the selected transport',
        );
      default:
        return new SessionError('network', e.message);
    }
  }
  return new SessionError('unknown', e instanceof Error ? e.message : String(e));
}

/** Resolve once the client is welcomed; reject if it is rejected, disconnects, or times out. */
export function awaitTableJoined<V, A, Cfg>(
  client: { getState(): TableClientState<V, A, Cfg>; subscribe(l: () => void): () => void },
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;
    const onAbort = () => finish(cancelled());
    const finish = (err?: Error) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsub();
      signal?.removeEventListener('abort', onAbort);
      if (err) reject(err);
      else resolve();
    };
    if (signal?.aborted) {
      done = true;
      reject(cancelled());
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
    const check = () => {
      const s = client.getState();
      if (s.status === 'joined') finish();
      else if (s.status === 'rejected') {
        finish(
          new SessionError(
            'rejected',
            rejectMessage(s.rejectReason, s.snapshot?.seats.length ?? null),
          ),
        );
      } else if (s.status === 'disconnected') {
        const why = s.error?.code === 'disconnected' ? ` (${s.error.message})` : '';
        finish(
          new SessionError('disconnected', `The connection closed before the game started${why}`),
        );
      }
    };
    const timer = setTimeout(
      () => finish(new SessionError('timeout', 'The host did not answer in time')),
      timeoutMs,
    );
    const unsub = client.subscribe(check);
    check();
  });
}

function makeDisposer(parts: {
  client: { close(): void };
  server?: { close(): void };
  listener?: Listener;
  unsub?: () => void;
}): () => void {
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const step of [
      () => parts.unsub?.(),
      () => parts.client.close(),
      () => parts.listener?.close(),
      () => parts.server?.close(),
    ]) {
      try {
        step();
      } catch {
        /* ignore */
      }
    }
  };
}

async function hostWithServer<S, A, C, V, Cfg>(
  def: GameDefinition<S, A, C, V, Cfg>,
  server: TableServer<S, A, C, V, Cfg>,
  code: string,
  profile: PlayerProfile,
  signer: Signer | undefined,
  deps: TableSessionDeps<S, A, C, V, Cfg>,
  signal?: AbortSignal,
): Promise<TableSession<S, A, C, V, Cfg>> {
  const createClient = deps.createClient ?? ((o) => new TableClient<S, A, Cfg, V>(o));
  let listener: Listener;
  try {
    throwIfAborted(signal);
    listener = await deps.provider.host(code);
  } catch (e) {
    server.close();
    throw mapTableTransportError(e);
  }
  if (signal?.aborted) {
    listener.close();
    server.close();
    throw cancelled();
  }
  const unsub = listener.onConnection((t) => server.accept(t));
  const local = server.connectLocal();
  const client = createClient({ transport: local, profile, signer, store: deps.store });
  logStatusChanges(client, `${code} host`);
  const dispose = makeDisposer({ client, server, listener, unsub });
  // The other player re-hosted this table while we were away: stop hosting so the screen can
  // rejoin them instead of both devices waiting at their own copy of the table.
  let lostAddress = false;
  listener.onLost?.(() => {
    lostAddress = true;
    connLog(`${code} session closed`, { reason: 'another device took the room code' });
    dispose();
  });
  try {
    await awaitTableJoined(client, deps.joinTimeoutMs ?? DEFAULT_JOIN_TIMEOUT, signal);
    throwIfAborted(signal);
  } catch (e) {
    dispose();
    throw mapTableTransportError(e);
  }
  const snapshot = client.getState().snapshot ?? server.getSnapshot();
  return {
    matchId: snapshot.id,
    code,
    role: 'host',
    client,
    server,
    provider: deps.provider,
    gameId: def.id,
    dispose,
    get lostAddress() {
      return lostAddress;
    },
  };
}

export interface HostTableOptions<Cfg> {
  profile: PlayerProfile;
  signer?: Signer;
  /** Abort while hosting; whatever was created is disposed. */
  signal?: AbortSignal;
  config: Cfg;
  /** Number of seats (clamped to the definition's range). */
  seats?: number;
  /** Seat the host takes; ignored when `dealer` is set. Default 0. */
  hostSeat?: number;
  /** Host as a non-playing dealer: every seat is filled by guests (`hostSeat: null`). */
  dealer?: boolean;
  /** Where the table's randomness comes from and how it is bound to actions. */
  randomness?: RandomnessChoice;
  /**
   * Unattended play: the table deals, starts the next hand and settles by itself, reacting to
   * seats, presence and readiness. Default: on (dealer-hosted tables are always unattended
   * unless this is explicitly false).
   */
  autopilot?: boolean;
  /** Table-level options the game does not interpret. */
  options?: Record<string, unknown>;
}

/** The `entropy` server option for a choice (undefined when none was made). */
export function entropyFor(
  choice: RandomnessChoice | undefined,
): { source: EntropySource; fallback: boolean } | undefined {
  if (!choice) return undefined;
  const built = buildEntropy(choice);
  return { source: built.source, fallback: built.fallback };
}

/**
 * Host with the rules the table is pinned to (`options.runtime`; this app's own for a new table):
 * that runtime's table server and its copy of the game's definition.
 */
async function pinnedTableServer<S, A, C, V, Cfg>(
  o: TableServerOptions<S, A, C, V, Cfg>,
): Promise<TableServer<S, A, C, V, Cfg>> {
  const pin = o.snapshot?.options?.runtime;
  const rt = await runtimeFor(typeof pin === 'string' ? pin : undefined);
  const def = (rt.definitions as Record<string, unknown>)[o.def.id] as typeof o.def | undefined;
  return rt.TableServer.create<S, A, C, V, Cfg>({ ...o, def: def ?? o.def });
}

/** Create a brand-new table: run the server here and connect our own client to it. */
export async function hostTable<S, A, C, V, Cfg>(
  def: GameDefinition<S, A, C, V, Cfg>,
  rawOpts: HostTableOptions<Cfg>,
  deps: TableSessionDeps<S, A, C, V, Cfg>,
): Promise<TableSession<S, A, C, V, Cfg>> {
  const profile = publicProfile(rawOpts.profile);
  requireName(profile);
  const createServer = deps.createServer ?? pinnedTableServer<S, A, C, V, Cfg>;
  const code = generateRoomCode();
  const randomness = rawOpts.randomness;
  const server = await createServer({
    def,
    code,
    host: profile,
    hostSeat: rawOpts.dealer ? null : (rawOpts.hostSeat ?? 0),
    seats: rawOpts.seats,
    config: rawOpts.config,
    options: {
      ...(rawOpts.options ?? {}),
      ...(randomness ? { randomness: randomnessOptions(randomness) } : {}),
      autopilot: rawOpts.autopilot ?? true,
      ...(CURRENT_RUNTIME !== 'dev' ? { runtime: CURRENT_RUNTIME } : {}),
      mailboxKey: newMailboxKey(),
    },
    ...(randomness ? { entropy: entropyFor(randomness) } : {}),
  });
  return hostWithServer(def, server, code, profile, rawOpts.signer, deps, rawOpts.signal);
}

export interface JoinTableOptions<S, A, Cfg> {
  code: string;
  profile: PlayerProfile;
  signer?: Signer;
  /** Our persisted copy, offered to the host (only useful for games without hidden information). */
  resumeSnapshot?: TableSnapshot<S, A, Cfg>;
  /** Tries before giving up when the host does not answer (default 1). */
  attempts?: number;
}

async function joinOnce<S, A, C, V, Cfg>(
  def: GameDefinition<S, A, C, V, Cfg>,
  rawOpts: JoinTableOptions<S, A, Cfg>,
  deps: TableSessionDeps<S, A, C, V, Cfg>,
  signal?: AbortSignal,
): Promise<TableSession<S, A, C, V, Cfg>> {
  const profile = publicProfile(rawOpts.profile);
  const createClient = deps.createClient ?? ((o) => new TableClient<S, A, Cfg, V>(o));
  const timeoutMs = deps.joinTimeoutMs ?? DEFAULT_JOIN_TIMEOUT;
  let resumeSnapshot = rawOpts.resumeSnapshot;
  if (!resumeSnapshot && !def.hiddenInformation && deps.store?.findByCode) {
    try {
      resumeSnapshot = await deps.store.findByCode(rawOpts.code);
    } catch {
      /* ignore lookup failures */
    }
  }
  // A seat's redacted view can never be adopted by the host; do not even offer it.
  if (resumeSnapshot && (def.hiddenInformation || resumeSnapshot.view)) resumeSnapshot = undefined;
  let transport;
  try {
    throwIfAborted(signal);
    transport = await deps.provider.join(rawOpts.code, { timeoutMs });
  } catch (e) {
    throw mapTableTransportError(e);
  }
  if (signal?.aborted) {
    transport.close();
    throw cancelled();
  }
  const client = createClient({
    transport,
    profile,
    signer: rawOpts.signer,
    store: deps.store,
    resumeSnapshot,
  });
  logStatusChanges(client, `${rawOpts.code} guest`);
  watchPin(client, (s) => s.snapshot?.options?.runtime);
  const dispose = makeDisposer({ client });
  try {
    await awaitTableJoined(client, timeoutMs, signal);
    throwIfAborted(signal);
  } catch (e) {
    dispose();
    throw mapTableTransportError(e);
  }
  const snapshot = client.getState().snapshot!;
  return {
    matchId: snapshot.id,
    code: rawOpts.code,
    role: 'guest',
    client,
    provider: deps.provider,
    gameId: def.id,
    dispose,
  };
}

/** Join a table someone else is hosting, retrying while the host may not be there yet. */
export async function joinTable<S, A, C, V, Cfg>(
  def: GameDefinition<S, A, C, V, Cfg>,
  rawOpts: JoinTableOptions<S, A, Cfg>,
  deps: TableSessionDeps<S, A, C, V, Cfg>,
  flow: FlowOptions = {},
): Promise<TableSession<S, A, C, V, Cfg>> {
  requireName(publicProfile(rawOpts.profile));
  return joinWithRetry(
    () => joinOnce(def, rawOpts, deps, flow.signal),
    rawOpts.attempts ?? 1,
    flow,
    deps,
  );
}

export interface ResumeTableOptions<S, A, Cfg> {
  snapshot: TableSnapshot<S, A, Cfg>;
  profile: PlayerProfile;
  signer?: Signer;
  /** Randomness source to use when re-hosting (the mode comes from the saved table). */
  randomness?: RandomnessChoice;
}

const QUICK_JOIN_MS = 4_000;
const QUICK_JOIN_TRIES = 3;
const QUICK_JOIN_GAP_MS = 1_000;

/**
 * Right after a table goes on by mail, look for its host every 5 s for 30 s (it may only have
 * been reloading): reaching the host's room code means it is hosting live again, and the relay
 * steps aside so the screen rejoins. After that, a host coming back reaches us by push. This only
 * touches the peer-to-peer signalling, not the mailbox. Returns the stop function.
 */
function probeHost(provider: TransportProvider, code: string, relay: MailRelay): () => void {
  const started = Date.now();
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const next = () => {
    if (stopped || relay.steppedAside) return;
    if (Date.now() - started + 5_000 > 30_000) return;
    timer = setTimeout(() => void probe(), 5_000);
  };
  const probe = async () => {
    if (stopped || relay.steppedAside) return;
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      try {
        const t = await provider.join(code, { timeoutMs: 5_000 });
        t.close();
        if (!stopped) relay.stepAside();
        return;
      } catch {
        /* still away */
      }
    }
    next();
  };
  next();
  return () => {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
  };
}

/** Name of the seat that hosted a snapshot, for messages. */
export function hostNameOf(snapshot: TableSnapshot): string {
  if (snapshot.hostSeat === null) return snapshot.dealer?.name ?? 'the dealer';
  return snapshot.seats[snapshot.hostSeat]?.name ?? 'the host';
}

/**
 * Resume a saved table. A full copy (ours when we hosted, or any copy of a game without hidden
 * information) is re-hosted under its room code, or joined when someone already hosts it. A
 * seat's redacted view cannot run a server, so it can only rejoin the host — and reports
 * `host-offline` while the host is not there. Stalled signalling is retried with backoff; pass
 * `flow.signal` to abort and `flow.onProgress` to show what is happening. If this tab already
 * runs a live session under the code, that session is returned untouched.
 */
export async function resumeTable<S, A, C, V, Cfg>(
  def: GameDefinition<S, A, C, V, Cfg>,
  rawOpts: ResumeTableOptions<S, A, Cfg>,
  deps: TableSessionDeps<S, A, C, V, Cfg>,
  flow: FlowOptions = {},
): Promise<TableSession<S, A, C, V, Cfg>> {
  const profile = publicProfile(rawOpts.profile);
  requireName(profile);
  const { snapshot } = rawOpts;
  const existing = deps.existingSession?.(snapshot.code);
  if (existing) return existing;
  const viewOnly = !!snapshot.view;
  const hostOnce = viewOnly
    ? undefined
    : async () => {
        const live = deps.existingSession?.(snapshot.code);
        if (live) return live;
        const createServer = deps.createServer ?? pinnedTableServer<S, A, C, V, Cfg>;
        let server: TableServer<S, A, C, V, Cfg>;
        try {
          server = await createServer({
            def,
            snapshot,
            code: snapshot.code,
            host: profile,
            ...(rawOpts.randomness ? { entropy: entropyFor(rawOpts.randomness) } : {}),
          });
        } catch (e) {
          throw new SessionError(
            'unknown',
            `The saved copy could not be verified: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
        return hostWithServer(
          def,
          server,
          snapshot.code,
          profile,
          rawOpts.signer,
          deps,
          flow.signal,
        );
      };
  const joinOnceStep = async () => {
    // Never join a code this tab is hosting: that would seat us as a guest of our own server.
    const live = deps.existingSession?.(snapshot.code);
    if (live) return live;
    return joinOnce(
      def,
      { code: snapshot.code, profile, signer: rawOpts.signer, resumeSnapshot: snapshot },
      deps,
      flow.signal,
    );
  };
  // A seat's view of a table played by mail: try the host once, and play by mail if it is away.
  const seat = snapshot.seats.findIndex((p) => p?.id === profile.id);
  if (
    viewOnly &&
    deps.byMail &&
    seat >= 0 &&
    snapshot.hostSeat !== null &&
    playedByMail(snapshot as TableSnapshot)
  ) {
    // A host that is only reloading is back in a moment: a few short tries before going by mail.
    const quick = {
      ...deps,
      joinTimeoutMs: Math.min(deps.joinTimeoutMs ?? QUICK_JOIN_MS, QUICK_JOIN_MS),
    };
    for (let attempt = 0; attempt < QUICK_JOIN_TRIES; attempt++) {
      try {
        const live = deps.existingSession?.(snapshot.code);
        if (live) return live;
        return await joinOnce(
          def,
          { code: snapshot.code, profile, signer: rawOpts.signer, resumeSnapshot: snapshot },
          quick,
          flow.signal,
        );
      } catch (e) {
        if (e instanceof SessionError && (e.code === 'cancelled' || e.code === 'rejected')) throw e;
      }
      await (deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms))))(
        QUICK_JOIN_GAP_MS,
      );
    }
    const relay = await deps.byMail(snapshot, seat);
    if (relay) {
      const session = await joinOnce(
        def,
        { code: snapshot.code, profile, signer: rawOpts.signer },
        { ...deps, provider: relay.provider() },
        flow.signal,
      );
      relays.set(session.matchId, relay);
      const stopProbe = probeHost(deps.provider, snapshot.code, relay);
      const dispose = session.dispose;
      connLog(`${snapshot.code} playing by mail`, { host: hostNameOf(snapshot) });
      // The host may only be reloading: look for their "back live" mail quickly for a while.
      globalThis.dispatchEvent?.(new Event(CHECK_MAIL_EVENT));
      return {
        ...session,
        byMail: relay,
        get lostAddress() {
          return relay.steppedAside;
        },
        dispose: () => {
          if (relays.get(session.matchId) === relay) relays.delete(session.matchId);
          stopProbe();
          relay.close();
          dispose();
        },
      };
    }
  }
  return resumeWithRetry(
    { hostOnce, joinOnce: joinOnceStep, hostName: hostNameOf(snapshot) },
    flow,
    deps,
  );
}
